import { zFightingFixDistance } from "@dextall/panels-defaults";
import customPanelsColorFactory from "../colors/customComponentColorsFactory";
import { claddingCellGap, editorCladdingGeometryThickness } from "../defaults";
import { calculateFacadeOrientation, type CardinalDirection } from "../geometry/compass";
import { FacadeElementType } from "./facadeElementType";
import type { ICustomZShapedPanelSource } from "../../../responses/customZShapedPanelSource";
import type { IModelCorner } from "../../../responses/modelCorner";
import type { ExtrusionGeometry } from "../geometry/claddingCellExtrusionGeometryFactory";
import type { ZShapeCladdingCellGeometry } from "../geometry/zShapeCladdingCellGeometry";
import type { Depth, InstallationSequence, PanelParameters } from "./panelParameters";
import type { ZShapeGeometryType } from "@dextall/corner-geometry";

export class CustomZShapedPanel {
    readonly leftWingFacadeOrientation: CardinalDirection;
    readonly shelfFacadeOrientation: CardinalDirection;
    readonly rightWingFacadeOrientation: CardinalDirection;
    private readonly material = customPanelsColorFactory.getDefaultCornerMaterial();
    private readonly _depth: Depth;
    private _installationSequence: InstallationSequence;

    constructor(
        private readonly panelSource: ICustomZShapedPanelSource,
        private readonly geometry: ZShapeCladdingCellGeometry,
        private readonly modelCorner: IModelCorner,
        private readonly modelOffset: THREE.Vector3,
        public readonly panelDbId: number,
        public readonly internalId?: string,
    ) {
        this._installationSequence = panelSource.installationSequence;
        this._depth = panelSource.depth;
        // this id is used to keep original corner id after undo or redo actions with corner
        this.internalId = internalId || panelSource.id;

        const origin = this.computeGeometryOrigin(tempVector);
        const leftWingMatrix = this.geometry.getWingMatrix(origin, "left-wing");
        const shelfMatrix = this.geometry.getWingMatrix(origin, "shelf");
        const rightWingMatrix = this.geometry.getWingMatrix(origin, "right-wing");

        this.leftWingFacadeOrientation = calculateFacadeOrientation(tempVector.setFromMatrixColumn(2, leftWingMatrix));
        this.shelfFacadeOrientation = calculateFacadeOrientation(tempVector.setFromMatrixColumn(2, shelfMatrix));
        this.rightWingFacadeOrientation = calculateFacadeOrientation(
            tempVector.setFromMatrixColumn(2, rightWingMatrix),
        );
    }

    get id(): string {
        return this.panelSource.id;
    }

    get elementType(): FacadeElementType.CustomZShaped {
        return FacadeElementType.CustomZShaped;
    }

    get installationSequence(): InstallationSequence {
        return this._installationSequence;
    }

    get depth(): Depth {
        return this._depth;
    }

    get customPanelTypeId(): string {
        return this.panelSource.customZShapedPanelTypeId;
    }

    get wallCornerId(): string {
        return this.panelSource.wallCornerId;
    }

    get shelfEndWallCornerId(): string {
        return this.panelSource.shelfEndWallCornerId;
    }

    get userUniqueId(): number {
        return this.panelSource.userUniqueId;
    }

    get heightIndex(): number {
        return this.panelSource.heightIndex;
    }

    get offset(): number {
        return this.panelSource.offset;
    }

    get angle1(): number {
        return this.panelSource.angle1;
    }

    get angle2(): number {
        return this.panelSource.angle2;
    }

    get leftWing(): number {
        return this.panelSource.leftWingLength;
    }

    get shelf(): number {
        return this.panelSource.shelfLength;
    }

    get rightWing(): number {
        return this.panelSource.rightWingLength;
    }

    get height(): number {
        return this.panelSource.height;
    }

    get elementName(): string {
        return this.panelSource.elementName;
    }

    createGeometry(): ExtrusionGeometry[] {
        const origin = this.computeGeometryOrigin(tempVector);

        return this.geometry.createGeometry(origin, this.material, this.material, this.material);
    }

    getCoordinateSystem(): THREE.Matrix4 {
        const origin = this.computeGeometryOrigin(tempVector);

        return this.geometry.getCornerCoordinateSystem(origin);
    }

    getLeftWingWallCoordinateSystem(): THREE.Matrix4 {
        return this.geometry.getWingMatrix(this.computeGeometryOrigin(tempVector), "left-wing");
    }

    getRightWingWallCoordinateSystem(): THREE.Matrix4 {
        return this.geometry.getWingMatrix(this.computeGeometryOrigin(tempVector), "right-wing");
    }

    getShelfCoordinateSystem(): THREE.Matrix4 {
        return this.geometry.getWingMatrix(this.computeGeometryOrigin(tempVector), "shelf");
    }

    computeTitleMatrix(titleSize: THREE.Vector3, geometryType: ZShapeGeometryType): THREE.Matrix4 {
        const origin = this.computeGeometryOrigin(tempVector);

        const wingMatrix = this.geometry.getWingMatrix(origin, geometryType);

        const wingLength = this.getWingLength(geometryType);

        const position = new THREE.Vector3(
            0.5 * (wingLength - titleSize.x),
            0.5 * (this.height - titleSize.y),
            zFightingFixDistance,
        ).applyMatrix4(wingMatrix);

        return wingMatrix.setPosition(position);
    }

    getWorldOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        const origin = this.computeGeometryOrigin(tempVector);

        const shelfMatrix = this.geometry.getWingMatrix(origin, "shelf");

        const shelfLength = this.getWingLength("shelf");

        targetVector.set(0.5 * shelfLength, -0.5 * editorCladdingGeometryThickness, 0);

        return targetVector.applyMatrix4(shelfMatrix);
    }

    getTargetOffsetFor(worldPoint: THREE.Vector3): number {
        const cornerOrigin = this.computeWallCornerOrigin(tempVector);

        return worldPoint.z - cornerOrigin.z;
    }

    setPanelType(targetPanelTypeId: string | { id: string }) {
        this.panelSource.customZShapedPanelTypeId = typeof targetPanelTypeId === "string"
            ? targetPanelTypeId
            : targetPanelTypeId.id;
    }

    setPanelWindowDesignParameters() {
        // No windows in custom Z-shaped panels
        console.error("Custom Z-shaped panels do not have windows!");
    }

    setLouvreCutouts() {
        // No louvres in custom Z-shaped panels
        console.error("Custom Z-shaped panels do not have louvres!");
    }

    update({ elementName, installationSequence }: PanelParameters) {
        this.panelSource.elementName = elementName;
        this._installationSequence = installationSequence;
    }

    setOffset(offset: number) {
        this.panelSource.offset = offset;
    }

    private getWingLength(geometryType: ZShapeGeometryType) {
        switch (geometryType) {
            case "left-wing":
                return this.leftWing;

            case "shelf":
                return this.shelf;

            case "right-wing":
                return this.rightWing;

            default:
                throw new Error("Invalid geometry type");
        }
    }

    private computeGeometryOrigin(targetVector?: THREE.Vector3, addCladdingCellGap = true): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        this.computeWallCornerOrigin(targetVector);

        targetVector.z += this.offset + (addCladdingCellGap ? claddingCellGap : 0);

        return targetVector;
    }

    private computeWallCornerOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        targetVector
            .copy(this.modelOffset)
            .add(this.modelCorner.origin);

        targetVector.z += this.modelCorner.heights[this.heightIndex];

        return targetVector;
    }
}

const tempVector = new THREE.Vector3();
